import { Order, OrderLeg } from '@tradingblock/types';

function gcdPair(a: number, b: number): number {
  return b === 0 ? a : gcdPair(b, a % b);
}

/** Greatest common divisor of a non-empty list of integers (local copy avoids importing GCD from utilities that pull the full API graph in Jest). */
function gcdOf(values: number[]): number {
  if (!values.length) {
    return 1;
  }
  return values.reduce((acc, v) => gcdPair(acc, v), values[0]);
}

/**
 * Some multi-leg custom orders arrive with the spread quantity duplicated: e.g. Quantity 5 and
 * SpreadRatio 5 on every leg for a 1:1 vertical. Naively multiplying gcd × quantity (preview)
 * or SpreadRatio × Quantity (duplicate / reverse from activity) then implies 25 contracts per leg.
 * When every leg ratio equals the order quantity, treat ratios as redundant scaling and normalize to 1:1.
 */
export function adjustCustomMultilegOrderQuantities(order: Order<OrderLeg>): {
  adjustedOverallQuantity: number;
  adjustedLegs: (OrderLeg & { Quantity?: number })[];
} {
  if (!order || !order.Legs || order.Legs.length < 2) {
    return {
      adjustedOverallQuantity: order && order.Quantity !== undefined ? order.Quantity : 0,
      adjustedLegs: (order && order.Legs ? order.Legs : []) as OrderLeg[],
    };
  }

  const Q = order.Quantity;
  const ratios = order.Legs.map(l => (l.SpreadRatio !== undefined && l.SpreadRatio !== null ? l.SpreadRatio : 1));
  const gcd = gcdOf(ratios) || 1;
  const allEqual = ratios.every(r => r === ratios[0]);

  if (ratios.length > 1 && allEqual && ratios[0] === Q && Q > 1) {
    console.debug(
      '[adjustCustomMultilegOrderQuantities] Normalizing redundant order Quantity / SpreadRatio encoding',
      { quantity: Q, ratios }
    );
    return {
      adjustedOverallQuantity: Q,
      adjustedLegs: order.Legs.map(l => ({
        ...l,
        SpreadRatio: 1,
        Quantity: Q,
      })),
    };
  }

  const adjustedOverallQuantity = Q * gcd;

  const adjustedLegs = order.Legs.map(l => {
    const ratio = l.SpreadRatio !== undefined && l.SpreadRatio !== null ? l.SpreadRatio : 1;
    const adjustedRatio = ratio / gcd;
    return {
      ...l,
      SpreadRatio: adjustedRatio,
      Quantity: adjustedOverallQuantity * adjustedRatio,
    };
  });

  return { adjustedOverallQuantity, adjustedLegs };
}

/**
 * Per-leg contract count when duplicating or reversing a custom strategy order from activity
 * (see adjustCustomMultilegOrderQuantities).
 */
export function getCustomDuplicateLegContractCount(
  orderQuantity: number,
  legSpreadRatio: number | undefined,
  allLegRatios: (number | undefined)[]
): number {
  const r = legSpreadRatio !== undefined && legSpreadRatio !== null ? legSpreadRatio : 1;
  const ratios = allLegRatios.map(x => (x !== undefined && x !== null ? x : 1));
  const gcd = gcdOf(ratios) || 1;

  if (orderQuantity === 1 && ratios.length > 1) {
    return r;
  }

  const allEqual = ratios.length > 0 && ratios.every(x => x === ratios[0]);
  if (ratios.length > 1 && allEqual && ratios[0] === orderQuantity && orderQuantity > 1) {
    console.debug(
      '[getCustomDuplicateLegContractCount] Redundant Quantity/SpreadRatio encoding; per-leg count equals order quantity',
      { orderQuantity, ratios }
    );
    return orderQuantity;
  }

  return (r * orderQuantity) / gcd;
}

export interface OrderCopyBuilderQuantityParams {
  /** Order.Quantity from the source order (activity, etc.). */
  sourceOrderQuantity: number;
  isSingleLeggedCustomOrder: boolean;
  /** True when strategy is Custom (including multi-leg from getStrategyForLegs). */
  isCustom: boolean;
  setCustom?: boolean;
  duplicateOrder?: boolean;
}

/**
 * Order-level quantity passed to OrderBuilder for copy/duplicate flows.
 * Multi-leg custom spreads store contracts on each leg's SpreadRatio; order quantity must be 1
 * when setCustom or duplicateOrder, or Est. totals multiply gcd × order qty incorrectly.
 */
export function getOrderCopyBuilderQuantity(params: OrderCopyBuilderQuantityParams): number {
  const { sourceOrderQuantity, isSingleLeggedCustomOrder, isCustom, setCustom, duplicateOrder } = params;
  if (isSingleLeggedCustomOrder) {
    return sourceOrderQuantity;
  }
  if (isCustom && (setCustom === true || duplicateOrder === true)) {
    return 1;
  }
  return sourceOrderQuantity;
}
